import { Box, Grid, GridItem, Heading, Tooltip, useBreakpointValue, useDisclosure } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import PhysicalRiskLocationsModal from "./PhysicalRiskLocationsModal";

function CustomHeatmap({ data, headers, colors, tooltipAttributes, containerId, clickHandling }) {
  const portalRef = useRef();
  const wideColumnBreakpoint = useBreakpointValue({ base: false, lg: true });
  const [wideColumnState, setWideColumnState] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalData, setModalData] = useState(undefined);

  const onModalClose = () => {
    setModalData(undefined);
    onClose();
  };

  const onClickHandler = (data) => {
    if (data && clickHandling && data[clickHandling.availableDataAttribute]) {
      setModalData(data[clickHandling.availableDataAttribute]);
      onOpen();
    }
  };

  useEffect(() => {
    setWideColumnState(wideColumnBreakpoint);
  }, [wideColumnBreakpoint]);

  const scrollListener = () => {
    const tooltipElement = document.getElementById("tooltipElement");
    if (tooltipElement) {
      // When scroll event is detected, the tooltip element is removed to prevent bug with the scrollbars
      tooltipElement.remove();
    }
  };

  useEffect(() => {
    const containerElement = document.getElementById(containerId);
    if (containerElement) {
      containerElement.addEventListener("scroll", scrollListener);
    }

    return (() => {
      containerElement.removeEventListener("scroll", scrollListener);
    });
  }, [containerId]);

  const renderHeaders = () => {
    return headers.map((header, index) => {
      return <GridItem
        key={`header-${header.attribute}`}
        paddingInline={2}
        colSpan={wideColumnState && index === 0 ? 2 : 1}
      >
        <Heading marginBlock="1" size="sm">
          {header.label}
        </Heading>
      </GridItem>;
    });
  };

  const renderRows = () => {
    return data.map(item => {
      return headers.map((header, index) => {
        const currentAttribute = header.attribute;
        const bgColor = !isNaN(item[currentAttribute]) ?
          colors.find(color => color.value === item[currentAttribute])?.color : "#efefef";
        let value = isNaN(item[currentAttribute]) ? item[currentAttribute] : "";

        const additionalAttributes = {
          style: {},
          onClick: null
        };
        if (clickHandling && currentAttribute === clickHandling.clickableAttribute &&
          item[clickHandling.availableDataAttribute]) {
          additionalAttributes.style = {
            textDecoration: "underline",
            cursor: "pointer"
          };
          additionalAttributes.onClick = onClickHandler;
        }

        const gridItem = <GridItem
          key={`grid-item-${item.id}-${currentAttribute}`}
          backgroundColor={bgColor}
          padding={value !== "" ? 2 : 0}
          colSpan={wideColumnState && index === 0 ? 2 : 1}
          style={{
            ...additionalAttributes.style,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
          onClick={additionalAttributes.onClick ? () => additionalAttributes.onClick(item) : null}
        >
          {value}
        </GridItem>;

        let tooltipLabel = header.label;

        if (value === "") {
          if (tooltipAttributes && tooltipAttributes.length > 0) {
            tooltipAttributes.forEach(attr => {
              tooltipLabel += `\n${item[attr]}`;
            });
          }
        } else {
          tooltipLabel += header.label !== "" ? `:\n${value}` : value;
        }

        return <Tooltip
          id={"tooltipElement"}
          key={`tooltip-${item.id}-${currentAttribute}`}
          whiteSpace={"pre-line"}
          label={tooltipLabel}
          portalProps={{ containerRef: portalRef }}
          hasArrow
        >
          {gridItem}
        </Tooltip>;
      });
    });
  };

  return (
    data.length > 0 && headers.length > 0 &&
    <Box id={containerId} height={500} overflow="auto" paddingRight={2} ref={portalRef}>
      <Grid
        templateColumns={"repeat(" + (wideColumnState ? headers.length + 1 : headers.length) + ", 1fr)"}
        gap={0.5}
        gridAutoRows={"minmax(64px, auto)"}
        gridAutoColumns={"minmax(64px, auto)"}
        minWidth={500}
      >
        {renderHeaders()}
        {renderRows()}
      </Grid>
      {clickHandling && isOpen && <PhysicalRiskLocationsModal
        modalData={modalData}
        isOpen={isOpen}
        onClose={onModalClose}
      />}
    </Box>
  );
}

export default CustomHeatmap;